/**
 * Production-ready reimagined Loan Detail page.
 *
 * Renders inside the `account-activity.loans.detail` route when the
 * `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, links to reimagined-only
 * routes (finance history, optimizer). Quick actions point to existing
 * production routes.
 */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import NavCard from 'tio-reimagined/components/nav-card';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import {
  PhBank,
  PhArrowLeft,
  PhCurrencyDollar,
  PhPercent,
  PhTrendDown,
  PhCheckCircle,
  PhCreditCard,
  PhSparkle,
  PhHandshake,
  PhStar,
  PhCloudArrowUp,
  PhTarget,
} from 'ember-phosphor-icons';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type AccountModel from 'tio-data/models/account';
import type PersonModel from 'tio-data/models/person';
import type LoanModel from 'tio-data/models/loan';

export interface ProductionLoanDetailModel {
  account: AccountModel | undefined;
  person: PersonModel;
  accountId: string;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
}

interface DetailSignature {
  Args: {
    model: ProductionLoanDetailModel;
  };
}

export default class ProductionLoanDetail extends Component<DetailSignature> {
  @service declare sessionContext: SessionContextService;

  get account() {
    return this.args.model.account;
  }

  get activeLoans(): LoanModel[] {
    return this.account?.activeLoans || [];
  }

  get servicerName() {
    return this.account?.institutionName || 'Loan Servicer';
  }

  get totalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.currentBalance || 0), 0);
  }

  get totalOriginalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.originalAmount || 0), 0);
  }

  get progressPercent() {
    if (!this.totalOriginalBalance) return 0;
    const rawPaid = this.totalOriginalBalance - this.totalBalance;
    const paid = Math.max(0, Math.min(rawPaid, this.totalOriginalBalance));
    const percent = (paid / this.totalOriginalBalance) * 100;
    return Math.round(Math.max(0, Math.min(percent, 100)));
  }

  get weightedInterestRate() {
    return Number(this.account?.loanInterestAverage || 0);
  }

  get totalOriginalBalanceSubtext() {
    return `${formatCurrency(this.totalOriginalBalance)} original`;
  }

  get weightedInterestRateValue() {
    return `${formatRate(this.weightedInterestRate)}%`;
  }

  get paidOffValue() {
    return `${this.progressPercent}%`;
  }

  get paidOffSubtext() {
    return `${formatCurrency(sub(this.totalOriginalBalance, this.totalBalance))} paid down`;
  }

  get monthlyPayment() {
    return this.account?.loanMinMonthlyPayment || this.account?.providerRegularMonthlyPayment || 0;
  }

  get isReceivingContributions() {
    return this.account?.id === this.args.model.person.accountReceivingContributionsId;
  }

  get loanCount() {
    return this.activeLoans.length;
  }

  get heroSubtitle() {
    if (this.loanCount === 0) return 'No active loans found for this account.';
    return `${this.loanCount} active loan${this.loanCount !== 1 ? 's' : ''} with ${this.servicerName}. ${this.isReceivingContributions ? 'This account is receiving employer contributions.' : ''}`;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back Navigation }}
      <div class="mb-2">
        <LinkTo
          @route="authenticated.account-activity.accounts"
          class="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <PhArrowLeft @size={{16}} />
          Back to Loan Summary
        </LinkTo>
      </div>

      {{! Hero }}
      <PageHero
        @icon={{PhBank}}
        @title={{this.servicerName}}
        @subtitle={{this.heroSubtitle}}
        @theme="indigo"
        role="banner"
      >
        <:stat>
          <div class="text-4xl font-bold">{{formatCurrency this.totalBalance}}</div>
          <Text class="text-indigo-100">Current Balance</Text>
        </:stat>
      </PageHero>

      {{! Status Badges }}
      <div class="flex items-center gap-3 mt-4 flex-wrap">
        {{#if this.isReceivingContributions}}
          <Chip @intent="success">
            <PhHandshake @size={{14}} />
            Receiving Contributions
          </Chip>
        {{/if}}
        <Chip @intent="default">
          {{this.loanCount}}
          Loan{{if (isGreater this.loanCount 1) "s"}}
        </Chip>
        {{#if this.account}}
          <Chip @intent="default">
            {{accountType this.activeLoans}}
          </Chip>
        {{/if}}
      </div>

      {{! Account Summary Stats }}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <StatCard
          @icon={{PhCurrencyDollar}}
          @label="Total Balance"
          @value={{formatCurrency this.totalBalance}}
          @subtext={{this.totalOriginalBalanceSubtext}}
          @color="indigo"
        />
        <StatCard
          @icon={{PhPercent}}
          @label="Avg. Interest Rate"
          @value={{this.weightedInterestRateValue}}
          @subtext="Weighted average"
          @color="blue"
        />
        <StatCard
          @icon={{PhCreditCard}}
          @label="Monthly Payment"
          @value={{formatCurrency this.monthlyPayment}}
          @subtext="Minimum required"
          @color="violet"
        />
        <StatCard
          @icon={{PhTrendDown}}
          @label="Paid Off"
          @value={{this.paidOffValue}}
          @subtext={{this.paidOffSubtext}}
          @color="emerald"
        >
          <:extra>
            <div class="flex items-center gap-1 text-emerald-600 text-sm">
              <PhCheckCircle @size={{14}} />
              <span>On track</span>
            </div>
          </:extra>
        </StatCard>
      </div>

      {{! Overall Progress Bar }}
      <div class="mt-6 bg-white rounded-xl p-6 shadow-sm ring-1 ring-gray-950/4">
        <div class="flex items-center justify-between mb-3">
          <Heading @level={{3}} class="text-base font-semibold text-gray-900">
            Payoff Progress
          </Heading>
          <Text class="text-sm text-gray-500">{{this.progressPercent}}% complete</Text>
        </div>
        <ProgressBar @value={{this.progressPercent}} @color="indigo" @size="lg" />
        <div class="flex justify-between mt-2">
          <Text class="text-xs text-gray-400">{{formatCurrency this.totalOriginalBalance}}
            original</Text>
          <Text class="text-xs text-gray-400">{{formatCurrency this.totalBalance}} remaining</Text>
        </div>
      </div>

      {{! Individual Loans List }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhStar}}
          @title="Individual Loans"
          @iconClass="text-indigo-500"
          @iconSize={{24}}
        />
        <div class="space-y-3">
          {{#each this.activeLoans as |loan index|}}
            <div class="bg-white rounded-xl p-5 shadow-sm ring-1 ring-gray-950/4">
              <div class="flex items-start justify-between gap-4">
                <div class="flex items-center gap-3">
                  <div class="p-2 rounded-lg {{if (isEven index) 'bg-indigo-100' 'bg-blue-100'}}">
                    <PhCurrencyDollar
                      @size={{20}}
                      class="{{if (isEven index) 'text-indigo-600' 'text-blue-600'}}"
                    />
                  </div>
                  <div>
                    <Text class="font-medium text-gray-900">
                      {{loanTypeName loan}}
                    </Text>
                    {{#if loan.number}}
                      <Text class="text-sm text-gray-500">····{{lastFour loan.number}}</Text>
                    {{/if}}
                  </div>
                </div>
                <div class="text-right">
                  <Text class="text-lg font-semibold text-gray-900">
                    {{formatCurrency loan.currentBalance}}
                  </Text>
                  <Text class="text-sm text-gray-500">
                    {{formatRate (loanInterestRate loan)}}% APR
                  </Text>
                </div>
              </div>

              {{! Loan detail metrics }}
              <div class="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div class="bg-gray-50 rounded-lg p-3">
                  <Text class="text-xs text-gray-500 mb-0.5">Original</Text>
                  <Text class="text-sm font-medium text-gray-900">
                    {{formatCurrency loan.originalAmount}}
                  </Text>
                </div>
                <div class="bg-gray-50 rounded-lg p-3">
                  <Text class="text-xs text-gray-500 mb-0.5">Principal</Text>
                  <Text class="text-sm font-medium text-gray-900">
                    {{formatCurrency (principalBalance loan)}}
                  </Text>
                </div>
                <div class="bg-gray-50 rounded-lg p-3">
                  <Text class="text-xs text-gray-500 mb-0.5">Interest</Text>
                  <Text class="text-sm font-medium text-gray-900">
                    {{formatCurrency (interestBalance loan)}}
                  </Text>
                </div>
                <div class="bg-gray-50 rounded-lg p-3">
                  <Text class="text-xs text-gray-500 mb-0.5">Loan Type</Text>
                  <Text class="text-sm font-medium text-gray-900 truncate">
                    {{loanClassLabel loan}}
                  </Text>
                </div>
              </div>

              {{! Progress for individual loan }}
              {{#if loan.originalAmount}}
                <div class="mt-3">
                  <ProgressBar
                    @value={{loanProgressPercent loan}}
                    @color={{if (isEven index) "indigo" "blue"}}
                    @size="sm"
                  />
                  <Text class="text-xs text-gray-400 mt-1">
                    {{loanProgressPercent loan}}% paid off
                  </Text>
                </div>
              {{/if}}
            </div>
          {{else}}
            <div class="bg-gray-50 rounded-xl p-8 text-center">
              <PhBank @size={{48}} class="mx-auto text-gray-300 mb-3" />
              <Text class="text-gray-500">No active loans found for this account.</Text>
            </div>
          {{/each}}
        </div>
      </div>

      {{! Quick Actions }}
      <div class="mt-6">
        <SectionHeader
          @icon={{PhSparkle}}
          @title="Quick Actions"
          @iconClass="text-indigo-500"
          @iconSize={{24}}
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NavCard
            @layout="horizontal"
            @route="authenticated.slr.dashboard"
            @icon={{PhHandshake}}
            @title="Contribution History"
            @subtitle="View payment timeline"
            @color="violet"
          />

          <NavCard
            @layout="horizontal"
            @route="authenticated.repaying-student-debt.repayment-strategy-finder"
            @icon={{PhTarget}}
            @title="Strategy Finder"
            @subtitle="Find best repayment path"
            @color="emerald"
          />

          <NavCard
            @layout="horizontal"
            @route="authenticated.observability.upload"
            @query={{hash
              source="loan-detail"
              providers="nslds,statement"
              returnTo="authenticated.account-activity.accounts"
            }}
            @icon={{PhCloudArrowUp}}
            @title="Upload Documents"
            @subtitle="Update loan information"
            @color="blue"
          />
        </div>
      </div>
    </VStack>
  </template>
}

// --- Template helpers ---

function formatRate(rate: number): string {
  return (rate || 0).toFixed(2);
}

function isGreater(a: number, b: number): boolean {
  return a > b;
}

function sub(a: number, b: number): number {
  return a - b;
}

function isEven(index: number): boolean {
  return index % 2 === 0;
}

function lastFour(value: string): string {
  return (value || '').slice(-4);
}

function loanTypeName(loan: LoanModel): string {
  const loanType = loan.observation?.['loan-type'] || loan.observation?.['raw-loan-type'] || '';
  if (loanType) return loanType;
  const cls = loan.loanClass?.toLowerCase() || '';
  if (cls.includes('stafford')) return 'Stafford Loan';
  if (cls.includes('plus')) return 'PLUS Loan';
  if (cls.includes('private')) return 'Private Loan';
  if (cls.includes('consolidat')) return 'Consolidation Loan';
  return 'Student Loan';
}

function loanClassLabel(loan: LoanModel): string {
  return loan.loanClass || 'Unknown';
}

function loanInterestRate(loan: LoanModel): number {
  return loan.observation?.['interest-rate'] || 0;
}

function principalBalance(loan: LoanModel): number {
  return loan.observation?.['principal-balance'] || 0;
}

function interestBalance(loan: LoanModel): number {
  return loan.observation?.['interest-balance'] || 0;
}

function loanProgressPercent(loan: LoanModel): number {
  if (!loan.originalAmount) return 0;
  const paid = loan.originalAmount - (loan.currentBalance || 0);
  return Math.max(0, Math.min(100, Math.round((paid / loan.originalAmount) * 100)));
}

function accountType(loans: LoanModel[]): string {
  if (!loans.length) return 'Unknown';
  const cls = loans[0]?.loanClass?.toLowerCase() || '';
  if (cls.includes('private')) return 'Private';
  if (cls.includes('refinance')) return 'Refinanced';
  return 'Federal';
}
